/**
 * Thread Grouping Logic
 *
 * Groups multiple comment threads by the DOM element they're attached to.
 * This enables the adaptive stacking pattern:
 * - 1-3 threads: Stacked pins with diagonal offset
 * - 4+ threads: Grouped badge with picker
 */

import type { Comment, MatchResult } from './types';

/**
 * Represents a group of threads attached to the same element
 */
export interface ElementThreadGroup {
  /** Unique identifier for this element (based on matched element reference) */
  elementId: string;
  /** The matched DOM element */
  element: Element;
  /** Match result containing confidence level */
  matchResult: MatchResult;
  /** Parent comments (threads) attached to this element, sorted by created_at */
  threads: Comment[];
}

/**
 * Threshold for switching from stacked pins to grouped badge
 */
export const THREAD_GROUP_THRESHOLD = 4;

/**
 * Generate a stable ID for a matched element.
 * Uses the element's position in the document as a stable identifier.
 */
export function getElementId(element: Element): string {
  // Use a WeakMap-based approach would be ideal, but for serialization
  // we need a string. Use the element's path in the DOM.
  const path: string[] = [];
  let current: Element | null = element;

  while (current && current !== document.documentElement) {
    const parent: Element | null = current.parentElement;
    if (parent) {
      const siblings = Array.from(parent.children);
      const index = siblings.indexOf(current);
      path.unshift(`${current.tagName}[${index}]`);
    } else {
      path.unshift(current.tagName);
    }
    current = parent;
  }

  return path.join('/');
}

/**
 * Groups parent comments (threads) by the DOM element they match to.
 *
 * @param comments - All comments (including replies)
 * @param matchResults - Map of comment ID to match result
 * @returns Array of element groups, each containing threads for that element
 */
export function groupThreadsByElement(
  comments: Comment[],
  matchResults: Map<string, MatchResult>
): ElementThreadGroup[] {
  // Filter to parent comments only (not replies)
  const parentComments = comments.filter(c => c.threadId === null);

  // Group by matched element
  const elementGroups = new Map<string, {
    element: Element;
    matchResult: MatchResult;
    threads: Comment[];
  }>();

  for (const comment of parentComments) {
    const matchResult = matchResults.get(comment.id);

    // Skip orphaned or unmatched comments
    if (!matchResult || matchResult.level === 'orphaned') {
      continue;
    }

    const elementId = getElementId(matchResult.element);

    if (elementGroups.has(elementId)) {
      // Add to existing group
      elementGroups.get(elementId)!.threads.push(comment);
    } else {
      // Create new group
      elementGroups.set(elementId, {
        element: matchResult.element,
        matchResult,
        threads: [comment],
      });
    }
  }

  // Convert to array and sort threads within each group by created_at
  const groups: ElementThreadGroup[] = [];

  for (const [elementId, group] of elementGroups) {
    // Sort threads by creation date (oldest first for consistent numbering)
    const sortedThreads = [...group.threads].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    groups.push({
      elementId,
      element: group.element,
      matchResult: group.matchResult,
      threads: sortedThreads,
    });
  }

  // Sort groups by their first thread's creation date for consistent ordering
  groups.sort((a, b) => {
    const aTime = a.threads[0] ? new Date(a.threads[0].createdAt).getTime() : 0;
    const bTime = b.threads[0] ? new Date(b.threads[0].createdAt).getTime() : 0;
    return aTime - bTime;
  });

  return groups;
}

/**
 * Determines the display mode for a thread group
 */
export function getDisplayMode(group: ElementThreadGroup): 'stacked' | 'grouped' {
  return group.threads.length >= THREAD_GROUP_THRESHOLD ? 'grouped' : 'stacked';
}

/**
 * Calculates global pin numbers across all groups.
 * Returns a map of comment ID to pin number.
 */
export function calculatePinNumbers(groups: ElementThreadGroup[]): Map<string, number> {
  const pinNumbers = new Map<string, number>();
  let currentNumber = 1;

  for (const group of groups) {
    for (const thread of group.threads) {
      pinNumbers.set(thread.id, currentNumber);
      currentNumber++;
    }
  }

  return pinNumbers;
}
